import React, { useEffect, useState } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';

/**
 * AuthStart component - Initiates the consent flow
 * This page is opened in a popup when additional permissions are needed
 */
const AuthStart = () => {
    const [status, setStatus] = useState('Teams SDK wird initialisiert...');
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log("Auth Start: Component mounted");

        const startAuth = async () => {
            try {
                // Initialize Teams SDK
                console.log("Auth Start: Initializing SDK...");
                setStatus('Verbindung mit Teams...');
                await microsoftTeams.app.initialize();
                console.log("Auth Start: SDK Initialized");

                // Get Teams context
                setStatus('Benutzerkontext wird abgerufen...');
                const context = await microsoftTeams.app.getContext();
                console.log("Auth Start: Context retrieved", context);

                const clientId = process.env.REACT_APP_CLIENT_ID;
                if (!clientId) {
                    throw new Error("Missing REACT_APP_CLIENT_ID in environment variables.");
                }

                setStatus('Azure AD-Anmeldung wird vorbereitet...');
                const tenantId = context.user.tenant.id || 'common';
                const loginHint = context.user.loginHint;

                // Configure MSAL
                const msalConfig = {
                    auth: {
                        clientId: clientId,
                        authority: `https://login.microsoftonline.com/${tenantId}`,
                        navigateToLoginRequestUrl: false
                    },
                    cache: {
                        cacheLocation: "sessionStorage",
                    },
                };

                console.log("Auth Start: Importing MSAL...");
                const msal = await import('@azure/msal-browser');
                console.log("Auth Start: Creating MSAL instance...");
                const msalInstance = new msal.PublicClientApplication(msalConfig);
                await msalInstance.initialize();

                const scopes = ["User.Read", "User.Read.All", "Presence.Read.All", "Calendars.Read", "offline_access"];
                const scopesRequest = {
                    scopes: scopes,
                    redirectUri: window.location.origin + `/#/auth-end`,
                    loginHint: loginHint
                };

                console.log("Auth Start: Redirecting to AAD...", scopesRequest);
                setStatus('Weiterleitung zur Microsoft-Anmeldung...');

                // Watchdog: If nothing happens for 15s (e.g. redirect fails), close
                const watchdog = setTimeout(() => {
                    microsoftTeams.authentication.notifyFailure("Zeitüberschreitung bei Weiterleitung.");
                }, 15000);

                await msalInstance.loginRedirect(scopesRequest);
                // The browser redirects away here, so timeout won't fire unless it hangs
            } catch (err) {
                console.error('Auth Start Error:', err);
                setError(err.message || 'An unknown error occurred');
                setStatus('Authentifizierung fehlgeschlagen');

                // Essential: Notify Teams to close the popup if initialization fails
                try {
                    await microsoftTeams.app.initialize();
                    microsoftTeams.authentication.notifyFailure(err.message || 'Auth Start failed');
                } catch (e) {
                    console.error("Failed to notify Teams of start failure", e);
                }
            }
        };

        // Small delay to ensure Teams client is ready to receive the init call
        const timer = setTimeout(startAuth, 500);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div style={{
            padding: '40px',
            textAlign: 'center',
            fontFamily: 'Segoe UI, sans-serif',
            backgroundColor: '#f3f2f1',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {!error && <div className="loader" style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #6264a7',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                animation: 'spin 2s linear infinite',
                marginBottom: '20px'
            }} />}
            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            <h2 style={{ color: '#242424', marginBottom: '10px' }}>{status}</h2>

            {error ? (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#fde7e9',
                    color: '#a4262c',
                    borderRadius: '4px',
                    border: '1px solid #a4262c',
                    maxWidth: '80%'
                }}>
                    <strong>Fehler:</strong> {error}
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: '15px',
                            padding: '8px 16px',
                            backgroundColor: '#a4262c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'block',
                            width: '100%'
                        }}
                    >
                        Erneut versuchen
                    </button>
                </div>
            ) : (
                <p style={{ color: '#616161' }}>Bitte warten Sie, während die sichere Verbindung hergestellt wird.</p>
            )}
        </div>
    );
};

export default AuthStart;
